import React from 'react';
import { 
    Box, 
    Button, 
    Checkbox, 
    Dialog, 
    DialogContent, 
    DialogTitle, 
    FormControlLabel, 
    FormHelperText, 
    Grid, 
    IconButton, 
    List, 
    ListItem, 
    ListItemSecondaryAction, 
    MenuItem, 
    Paper, 
    TextField, 
    Typography 
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { CustomField, FieldType } from '../../../entities/business/LeakCheckFormTemplate';
import { useLeakCheckForm } from './LeakCheckFormContext';

interface FieldEditorDialogProps {
    open: boolean;
    onClose: () => void;
    field: CustomField;
    onSave: (field: CustomField) => void;
}

export const FieldEditorDialog: React.FC<FieldEditorDialogProps> = ({ 
    open, 
    onClose, 
    field, 
    onSave 
}) => {
    const [editedField, setEditedField] = React.useState<CustomField>({ ...field });
    const { addFieldOption, updateFieldOption, removeFieldOption } = useLeakCheckForm();

    React.useEffect(() => {
        setEditedField({ ...field });
    }, [field]);

    const handleSave = () => {
        onSave(editedField);
        onClose();
    };

    const handleAddOption = () => {
        setEditedField(addFieldOption(editedField));
    };

    const handleUpdateOption = (index: number, label: string) => {
        setEditedField(updateFieldOption(editedField, index, label));
    };

    const handleRemoveOption = (index: number) => {
        setEditedField(removeFieldOption(editedField, index));
    };

    const needsOptions = editedField.type === FieldType.Select || editedField.type === FieldType.Radio;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Field</DialogTitle>
            <DialogContent>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            label="Field Label"
                            value={editedField.label}
                            onChange={(e) => setEditedField({
                                ...editedField,
                                label: e.target.value
                            })}
                            fullWidth
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            select
                            label="Field Type"
                            value={editedField.type}
                            onChange={(e) => setEditedField({
                                ...editedField,
                                type: parseInt(e.target.value as string),
                                // Clear options if switching away from option type
                                options: parseInt(e.target.value as string) === FieldType.Select || 
                                         parseInt(e.target.value as string) === FieldType.Radio ? 
                                         editedField.options : []
                            })}
                            fullWidth
                            required
                        >
                            <MenuItem value={FieldType.Text}>Text</MenuItem>
                            <MenuItem value={FieldType.Number}>Number</MenuItem>
                            <MenuItem value={FieldType.Checkbox}>Checkbox</MenuItem>
                            <MenuItem value={FieldType.Select}>Dropdown</MenuItem>
                            <MenuItem value={FieldType.Radio}>Radio Buttons</MenuItem>
                            <MenuItem value={FieldType.TextArea}>Text Area</MenuItem>
                            <MenuItem value={FieldType.Date}>Date</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={editedField.isRequired}
                                    onChange={(e) => setEditedField({
                                        ...editedField,
                                        isRequired: e.target.checked
                                    })}
                                    color="primary"
                                />
                            }
                            label="Required Field"
                        />
                    </Grid>
                    
                    {editedField.type !== FieldType.Checkbox && (
                        <Grid item xs={12}>
                            <TextField
                                label="Default Value"
                                value={editedField.defaultValue || ''}
                                onChange={(e) => setEditedField({
                                    ...editedField,
                                    defaultValue: e.target.value
                                })}
                                fullWidth
                                helperText={editedField.type === FieldType.Date ? "Enter date in MM/DD/YYYY format" : ""}
                            />
                        </Grid>
                    )}
                    
                    {needsOptions && (
                        <Grid item xs={12}>
                            <Box mb={2} display="flex" alignItems="center" justifyContent="space-between">
                                <Typography variant="subtitle1">Options</Typography>
                                <Button 
                                    startIcon={<AddIcon />} 
                                    color="primary" 
                                    onClick={handleAddOption}
                                    disabled={editedField.options.length >= 10}
                                >
                                    Add Option
                                </Button>
                            </Box>
                            <Paper variant="outlined">
                                <List>
                                    {editedField.options.length === 0 && (
                                        <ListItem>
                                            <Typography color="textSecondary">
                                                No options added. Add at least two options.
                                            </Typography>
                                        </ListItem>
                                    )}
                                    {editedField.options.map((option, idx) => (
                                        <ListItem key={idx} dense>
                                            <TextField
                                                value={option.label}
                                                onChange={(e) => handleUpdateOption(idx, e.target.value)}
                                                fullWidth
                                                placeholder={`Option ${idx + 1}`}
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton 
                                                    edge="end" 
                                                    onClick={() => handleRemoveOption(idx)}
                                                    disabled={editedField.options.length <= 2}
                                                    size="small"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                            {editedField.options.length < 2 && (
                                <FormHelperText error>At least two options are required</FormHelperText>
                            )}
                        </Grid>
                    )}
                </Grid>
                
                <Box display="flex" justifyContent="flex-end" p={2} mt={2}>
                    <Button onClick={onClose} color="default" style={{ marginRight: 8 }}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSave} 
                        color="primary" 
                        variant="contained"
                        disabled={
                            !editedField.label.trim() || 
                            (needsOptions && editedField.options.length < 2)
                        }
                    >
                        Save
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};